/**
 * @fileoverview Evaluator Table
 * @description Searchable, band-filterable table of consolidated evaluator scores.
 * @module features/ScoutEval/components/EvaluatorTable
 */

import React, { useMemo, useState } from 'react';
import {
  Box,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { Eye, Search } from 'lucide-react';
import { ScoreChip } from './common';
import { BAND_FILTERS } from '../constants';

/**
 * Render the evaluator results table.
 * @param {Object} props - Evaluator rows and the handler that opens a detail tab.
 * @returns {JSX.Element} Results panel.
 */
export function EvaluatorTable({ evaluators, onOpenDetail }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [bandFilter, setBandFilter] = useState('all');

  const visibleRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return evaluators.filter((row) => (bandFilter === 'all' || row.band === bandFilter) && row.search.includes(term));
  }, [bandFilter, evaluators, searchTerm]);

  return (
    <Paper className="scout-panel" elevation={0}>
      <Box className="scout-toolbar">
        <Box>
          <Typography variant="h6">Evaluator results</Typography>
          <Typography className="scout-toolbar-count">
            Showing {visibleRows.length} of {evaluators.length} evaluators
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <TextField
            size="small"
            className="scout-search"
            placeholder="Search field, evaluator, framework"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            slotProps={{
              htmlInput: { 'aria-label': 'Search evaluators' },
              input: {
                startAdornment: (
                  <InputAdornment position="start"><Search size={15} /></InputAdornment>
                ),
              },
            }}
          />
          <ToggleButtonGroup
            exclusive
            size="small"
            value={bandFilter}
            onChange={(_event, next) => next && setBandFilter(next)}
            aria-label="Score band filter"
          >
            {BAND_FILTERS.map((filter) => (
              <ToggleButton key={filter.value} value={filter.value}>{filter.label}</ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>
      </Box>

      <TableContainer className="scout-table-container">
        <Table size="small" className="scout-table">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Report - Field</TableCell>
              <TableCell>AI Evaluator</TableCell>
              <TableCell>Framework</TableCell>
              <TableCell>Approach</TableCell>
              <TableCell align="center">Evaluation Score</TableCell>
              {onOpenDetail && <TableCell align="center"><span className="scout-visually-hidden">Detail</span></TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((row) => (
              <TableRow key={row.key} hover className={`scout-row scout-row--${row.band}`}>
                <TableCell className="scout-cell-index">{row.index}</TableCell>
                <TableCell className="scout-cell-field">{row.field}</TableCell>
                <TableCell>{row.evaluator}</TableCell>
                <TableCell className="scout-cell-muted">{row.framework}</TableCell>
                <TableCell className="scout-cell-muted">{row.approach}</TableCell>
                <TableCell align="center"><ScoreChip score={row.score} band={row.band} /></TableCell>
                {onOpenDetail && (
                  <TableCell align="center">
                    {row.detailId && (
                      <Tooltip title="Open detail report">
                        <IconButton
                          size="small"
                          className="scout-eye-link"
                          aria-label={`Open the detail report for ${row.field} ${row.evaluator}`}
                          onClick={() => onOpenDetail(row.detailId)}
                        >
                          <Eye size={15} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {visibleRows.length === 0 && (
        <Box className="scout-no-results">
          <Typography color="text.secondary">No evaluators match the current search and filter.</Typography>
        </Box>
      )}

      <Typography className="scout-legend">
        Score bands: green ≥ 0.85 · amber ≥ 0.75 · red &lt; 0.75.
        {onOpenDetail && ' Open the eye icon to jump to the related detail report.'}
      </Typography>
    </Paper>
  );
}

export default EvaluatorTable;
